import React from 'react';
import { Shield } from 'lucide-react';

import type { MemberRole } from '../types/api';
import { Chip } from './Chip';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import { formatNumber } from '../lib/formatters';
import { cn } from '../lib/utils';

/** The one label the system role wears, whatever its row is named. */
export const SYSTEM_ROLE_CHIP_LABEL = 'System administrator';

export interface RoleChipsProps {
  /** RBAC assignments only — never the legacy organization_users.role label. */
  roles: MemberRole[] | null | undefined;
  /** Chips shown before "+N". Default 2. */
  max?: number;
  className?: string;
}

interface ChipItem {
  key: string;
  label: string;
  system: boolean;
}

/** System access first (as one chip), then ordinary roles by name, then id. */
function chipItems(roles: MemberRole[]): ChipItem[] {
  const system = roles.some((r) => r.isSystemOnly);
  const ordinary = roles
    .filter((r) => !r.isSystemOnly)
    .sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id))
    .map((r) => ({ key: r.id, label: r.name, system: false }));
  return system
    ? [{ key: 'system', label: SYSTEM_ROLE_CHIP_LABEL, system: true }, ...ordinary]
    : ordinary;
}

/**
 * A person's roles in one organization: at most `max` muted chips, then "+N"
 * with a tooltip naming every role. System administrator access is one amber
 * Shield chip, consistent with the "System administrator access" switch.
 * Roles are not statuses, so nothing here goes through StatusBadge.
 */
export const RoleChips: React.FC<RoleChipsProps> = ({ roles, max = 2, className }) => {
  const items = chipItems(roles ?? []);
  if (items.length === 0) {
    return <span className={cn('text-sm text-muted-foreground', className)}>No roles</span>;
  }

  const shown = items.slice(0, Math.max(1, max));
  const hidden = items.slice(shown.length);

  return (
    <span className={cn('flex min-w-0 flex-wrap items-center gap-1', className)} data-role-chips="">
      {shown.map((item) => (
        <Chip
          key={item.key}
          variant={item.system ? 'warning' : 'muted'}
          icon={item.system ? Shield : undefined}
        >
          {item.label}
        </Chip>
      ))}
      {hidden.length > 0 ? (
        <Tooltip>
          <TooltipTrigger asChild>
            {/* data-row-action: a focusable trigger inside a row never opens the row. */}
            <span
              tabIndex={0}
              data-row-action=""
              className="rounded-full text-xs font-medium text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span aria-hidden="true">+{formatNumber(hidden.length)}</span>
              <span className="sr-only">
                and {formatNumber(hidden.length)} more: {hidden.map((item) => item.label).join(', ')}
              </span>
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            {items.map((item) => item.label).join(', ')}
          </TooltipContent>
        </Tooltip>
      ) : null}
    </span>
  );
};
